import { useState, useCallback, useEffect, useMemo } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Select } from '@/shared/components/ui/Select';
import type { FieldMappingEntry, FieldType } from './payrollTypes';

// ─── Known base payslip field options ─────────────────────────────────────────
const KNOWN_FIELDS = [
  { value: 'employee_name',                  label: 'Employee Name',                group: 'Identity' },
  { value: 'employee_id_code',               label: 'Employee ID / Code',           group: 'Identity' },
  { value: 'email',                          label: 'Email Address',                group: 'Identity' },
  { value: 'designation',                    label: 'Designation',                  group: 'Identity' },
  { value: 'bank_name',                      label: 'Bank Name',                    group: 'Bank' },
  { value: 'bank_account',                   label: 'Bank Account Number',          group: 'Bank' },
  { value: 'ifsc_code',                      label: 'IFSC Code',                    group: 'Bank' },
  { value: 'working_days',                   label: 'Working Days',                 group: 'Attendance' },
  { value: 'leave_days',                     label: 'Leave Days',                   group: 'Attendance' },
  { value: 'payslip_date',                   label: 'Payslip Date',                 group: 'Meta' },
  { value: 'earnings.Basic',                  label: '↑ Basic Pay',                  group: 'Earnings' },
  { value: 'earnings.HRA',                    label: '↑ HRA',                        group: 'Earnings' },
  { value: 'earnings.Travelling Allowance',   label: '↑ Travelling Allowance',       group: 'Earnings' },
  { value: 'earnings.Bonus',                  label: '↑ Bonus',                      group: 'Earnings' },
  { value: 'earnings.Special Allowance',      label: '↑ Special Allowance',          group: 'Earnings' },
  { value: 'earnings.Reimbursement',          label: '↑ Reimbursement',              group: 'Earnings' },
  { value: 'earnings.Other Earning',          label: '↑ Other Earning',              group: 'Earnings' },
  { value: 'earnings.Provident Fund',         label: '↑ Provident Fund (Employer)',  group: 'Earnings' },
  { value: 'deductions.Provident Tax',        label: '↓ Provident Tax',              group: 'Deductions' },
  { value: 'deductions.Income Tax',           label: '↓ Income Tax',                 group: 'Deductions' },
  { value: 'deductions.Professional Tax',     label: '↓ Professional Tax',           group: 'Deductions' },
  { value: 'deductions.Other Deduction',      label: '↓ Other Deduction',            group: 'Deductions' },
  { value: 'skip',                            label: '— Skip this column',           group: 'Skip' },
];

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'currency', label: 'Currency (₹)' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'skip', label: 'Skip' },
];

// ─── Smart auto-guessing for ANY column header ───────────────────────────────
export function guessField(col: string): { payslip_field: string; field_type: FieldType } {
  const cleanCol = col.trim();
  const lc = cleanCol.toLowerCase().replace(/[\s_-]+/g, ' ');

  if (/\b(employee #|employee id|emp id|emp #|employee code|emp code)\b/.test(lc)) {
    return { payslip_field: 'employee_id_code', field_type: 'text' };
  }
  if (/\b(employee name|emp name|name)\b/.test(lc)) {
    return { payslip_field: 'employee_name', field_type: 'text' };
  }
  if (/\b(email|mail)\b/.test(lc)) {
    return { payslip_field: 'email', field_type: 'text' };
  }
  if (/\b(doj|date of joining|joining date|payslip date|date)\b/.test(lc)) {
    return { payslip_field: 'payslip_date', field_type: 'date' };
  }
  if (/\b(designation|role|position|title)\b/.test(lc)) {
    return { payslip_field: 'designation', field_type: 'text' };
  }
  if (/\b(bank name|bank)\b/.test(lc)) {
    return { payslip_field: 'bank_name', field_type: 'text' };
  }
  if (/\b(bank account|account #|account no|ac no|a\/c)\b/.test(lc)) {
    return { payslip_field: 'bank_account', field_type: 'text' };
  }
  if (/\b(ifsc|ifsc code)\b/.test(lc)) {
    return { payslip_field: 'ifsc_code', field_type: 'text' };
  }
  if (/\b(working days|total working days|present days)\b/.test(lc)) {
    return { payslip_field: 'working_days', field_type: 'number' };
  }
  if (/\b(leave days|total leave days|absent days|leaves)\b/.test(lc)) {
    return { payslip_field: 'leave_days', field_type: 'number' };
  }

  // Deductions check: tax, deduction, pf, esi, loan, pt, tds, penalty, fine, advance
  if (/\b(tax|deduction|deducation|pf|esi|loan|pt|tds|penalty|fine|advance)\b/.test(lc) && !/\b(provident fund)\b/.test(lc)) {
    return { payslip_field: `deductions.${cleanCol}`, field_type: 'currency' };
  }

  // Default ANY other numeric column to an Earning section!
  return { payslip_field: `earnings.${cleanCol}`, field_type: 'currency' };
}

interface Props {
  open: boolean;
  headers: string[];
  initialMappings?: FieldMappingEntry[];
  onSave: (mappings: FieldMappingEntry[]) => void;
  onCancel: () => void;
  saving?: boolean;
}

export function PayrollFieldMappingModal({ open, headers, initialMappings, onSave, onCancel, saving }: Props) {
  // Determine effective header list
  const effectiveHeaders = useMemo(() => {
    if (headers && headers.length > 0) return headers;
    if (initialMappings && initialMappings.length > 0) return initialMappings.map(m => m.excel_column);
    return [];
  }, [headers, initialMappings]);

  const [rows, setRows] = useState<FieldMappingEntry[]>([]);

  // Synchronize state whenever open, headers, or initialMappings change
  useEffect(() => {
    if (open && effectiveHeaders.length > 0) {
      setRows(
        effectiveHeaders.map(col => {
          const saved = initialMappings?.find(m => m.excel_column.toLowerCase() === col.toLowerCase());
          if (saved) return saved;
          const guess = guessField(col);
          return { excel_column: col, payslip_field: guess.payslip_field, field_type: guess.field_type };
        })
      );
    }
  }, [open, effectiveHeaders, initialMappings]);

  const update = useCallback((idx: number, key: keyof FieldMappingEntry, val: string) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: val } as FieldMappingEntry;
      if (key === 'payslip_field') {
        if (val === 'skip') next[idx].field_type = 'skip';
        else if (val.startsWith('earnings.') || val.startsWith('deductions.')) next[idx].field_type = 'currency';
        else if (['working_days', 'leave_days'].includes(val)) next[idx].field_type = 'number';
        else if (val === 'payslip_date') next[idx].field_type = 'date';
        else next[idx].field_type = 'text';
      }
      return next;
    });
  }, []);

  // Dynamically build select options so custom earnings/deductions display with clean icons
  const fieldOptions = useMemo(() => {
    const list = [...KNOWN_FIELDS];
    rows.forEach(r => {
      if (r.payslip_field && !list.some(o => o.value === r.payslip_field)) {
        const label = r.payslip_field.startsWith('earnings.')
          ? `↑ ${r.payslip_field.replace('earnings.', '')}`
          : r.payslip_field.startsWith('deductions.')
            ? `↓ ${r.payslip_field.replace('deductions.', '')}`
            : r.payslip_field;
        list.push({ value: r.payslip_field, label, group: 'Custom' });
      }
    });
    return list.map(f => ({ value: f.value, label: f.label }));
  }, [rows]);

  return (
    <Modal open={open} onClose={onCancel} title="Map Excel Columns to Payslip Fields" size="lg">
      <div className="space-y-3">
        <p className="text-body-md text-on-surface-variant">
          We detected <strong>{effectiveHeaders.length} columns</strong> in your Excel file. Each column is automatically assigned to a category. You can adjust the mapping or change any field type below.
        </p>

        <div className="rounded-lg border border-border overflow-hidden max-h-[55vh] overflow-y-auto">
          <table className="w-full text-body-md">
            <thead className="bg-surface-variant text-on-surface-variant sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium w-1/3">Excel Column</th>
                <th className="px-4 py-2.5 text-left font-medium">Maps To Section / Field</th>
                <th className="px-4 py-2.5 text-left font-medium w-32">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline/40">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="p-4 text-center text-on-surface-variant">
                    No columns detected. Please select an Excel file first.
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={row.excel_column + idx} className={row.field_type === 'skip' ? 'opacity-40' : ''}>
                    <td className="px-4 py-2 font-mono text-body-sm font-semibold text-on-surface">{row.excel_column}</td>
                    <td className="px-4 py-2">
                      <Select
                        name={`field-${idx}`}
                        value={row.payslip_field}
                        onChange={e => update(idx, 'payslip_field', e.target.value)}
                        options={fieldOptions}
                        className="text-body-md"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <Select
                        name={`type-${idx}`}
                        value={row.field_type}
                        onChange={e => update(idx, 'field_type', e.target.value as FieldType)}
                        options={FIELD_TYPE_OPTIONS}
                        className="text-body-md"
                        disabled={row.payslip_field === 'skip'}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between text-body-sm text-on-surface-variant pt-1">
          <span>↑ Earnings add to gross pay &nbsp;·&nbsp; ↓ Deductions subtract from gross pay</span>
          <span className="font-medium text-primary">{rows.filter(r => r.field_type !== 'skip').length} mapped columns</span>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button onClick={() => onSave(rows)} loading={saving} disabled={rows.length === 0}>
            Save Mapping &amp; Continue
          </Button>
        </div>
      </div>
    </Modal>
  );
}
